'use client';

import { useState, useCallback, useRef, useEffect, createElement } from 'react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

type ConfirmVariant = 'danger' | 'warning' | 'default';

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
}

interface UseConfirmReturn {
  confirm: (_options: ConfirmOptions) => Promise<boolean>;
  ConfirmDialogElement: React.ReactElement | null;
}

/**
 * A hook that replaces `window.confirm()` with an async ConfirmDialog.
 *
 * Usage:
 *   const { confirm, ConfirmDialogElement } = useConfirm();
 *   // In handler:
 *   const ok = await confirm({ title: 'Delete?', variant: 'danger' });
 *   if (!ok) return;
 *   // ... proceed
 *   // In JSX:
 *   {ConfirmDialogElement}
 */
export function useConfirm(): UseConfirmReturn {
  const [state, setState] = useState<{
    open: boolean;
    options: ConfirmOptions;
  }>({
    open: false,
    options: { title: '' },
  });

  const resolveRef = useRef<((_value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      // If a confirmation is already open, cancel it before replacing it so
      // the previous caller isn't left awaiting forever.
      if (resolveRef.current) {
        resolveRef.current(false);
      }
      resolveRef.current = resolve;
      setState({ open: true, options });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setState(prev => ({ ...prev, open: false }));
  }, []);

  const handleCancel = useCallback(() => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setState(prev => ({ ...prev, open: false }));
  }, []);

  // If the component unmounts while a confirmation is pending, resolve it as
  // canceled so the caller can continue and the promise doesn't dangle.
  useEffect(() => {
    return () => {
      if (resolveRef.current) {
        resolveRef.current(false);
        resolveRef.current = null;
      }
    };
  }, []);

  const ConfirmDialogElement = state.open
    ? createElement(ConfirmDialog, {
        open: true,
        onConfirm: handleConfirm,
        onCancel: handleCancel,
        title: state.options.title,
        description: state.options.description,
        confirmLabel: state.options.confirmLabel ?? 'Confirm',
        cancelLabel: state.options.cancelLabel ?? 'Cancel',
        variant: state.options.variant ?? 'default',
      })
    : null;

  return { confirm, ConfirmDialogElement };
}
